import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { environment } from 'src/environments/environment';
import { HolidayCreateDto, HolidayDto, CenterInfo, CenterUpdateDto, WorkingDayBackend, WorkingDayUpdateDto, CompanyUpdateDto, UserUpdateDto, ServiceCategorySummary, ServiceCategoryCreateDto, ServiceCategoryUpdateDto } from '../interfaces/my-workshop/my-account.models';
import { JobPositionCreateDto, JobPositionDetail, JobPositionSummary, JobPositionUpdateDto } from '../interfaces/my-workshop/job-position.model';
import { LocalStorageService } from 'src/app/services/LocalStorage.service';
import { LocalStorageKeys } from 'src/app/core/enums/LocalStorageKeys';
import { UserData } from '../interfaces/my-workshop/profile.interface';

@Injectable({
  providedIn: 'root',
})
export class MyAccountService {
  private apiUrl = environment.identity_url;
  private apiBackend = environment.cafler_backoffice_back_url;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private localStorageService: LocalStorageService
  ) { }

  private getAuthHeaders(): Observable<HttpHeaders> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          throw new Error('Failed to acquire token');
        }
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });
        return Promise.resolve(headers) as unknown as Observable<HttpHeaders>;
      })
    );
  }

  private authGet<T>(url: string): Observable<T> {
    return this.getAuthHeaders().pipe(
      switchMap((headers) => this.http.get<T>(url, { headers }))
    );
  }

  private authPost<T, B = unknown>(url: string, body: B): Observable<T> {
    return this.getAuthHeaders().pipe(
      switchMap((headers) => this.http.post<T>(url, body, { headers }))
    );
  }

  private authPut<T, B = unknown>(url: string, body: B): Observable<T> {
    return this.getAuthHeaders().pipe(
      switchMap((headers) => this.http.put<T>(url, body, { headers }))
    );
  }

  private authDelete<T>(url: string): Observable<T> {
    return this.getAuthHeaders().pipe(
      switchMap((headers) => this.http.delete<T>(url, { headers }))
    );
  }

  createHoliday(payload: HolidayCreateDto): Observable<HolidayDto> {
    const url = `${this.apiBackend}/center/holidays`;
    return this.authPost<HolidayDto, HolidayCreateDto>(url, payload);
  }

  editHoliday(id: string, payload: HolidayCreateDto): Observable<HolidayDto> {
    const url = `${this.apiBackend}/center/holidays/${encodeURIComponent(id)}`;
    return this.authPut<HolidayDto, HolidayCreateDto>(url, payload);
  }

  deleteHoliday(id: string): Observable<void> {
    const url = `${this.apiBackend}/center/holidays/${encodeURIComponent(id)}`;
    return this.authDelete<void>(url);
  }

  getHolidays(): Observable<HolidayDto[]> {
    const url = `${this.apiBackend}/center/holidays`;
    return this.authGet<HolidayDto[]>(url);
  }

  getCenterInfo(): Observable<CenterInfo> {
    const url = `${this.apiBackend}/centers/me`;
    return this.authGet<CenterInfo>(url);
  }

  updateCenterInfo(payload: CenterUpdateDto): Observable<CenterInfo> {
    const url = `${this.apiBackend}/centers/me`;
    return this.authPut<CenterInfo, CenterUpdateDto>(url, payload);
  }

  getWorkingDays(): Observable<WorkingDayBackend[]> {
    const url = `${this.apiBackend}/center/service-schedules`;
    return this.authGet<WorkingDayBackend[]>(url);
  }

  updateWorkingDays(payload: WorkingDayUpdateDto[]): Observable<WorkingDayBackend[]> {
    const url = `${this.apiBackend}/center/service-schedules/upsert`;
    return this.authPut<WorkingDayBackend[], WorkingDayUpdateDto[]>(url, payload);
  }

  getCompanyData(): Observable<CenterInfo> {
    const url = `${this.apiUrl}/user/me/business`;
    return this.authGet<CenterInfo>(url);
  }

  updateCompanyData(payload: CompanyUpdateDto): Observable<CenterInfo> {
    const url = `${this.apiUrl}/user/me/business`;
    return this.authPut<CenterInfo, CompanyUpdateDto>(url, payload);
  }

  getUserDetail(): Observable<UserData> {
    const url = `${this.apiUrl}/user/me`;
    return this.authGet<UserData>(url);
  }

  updateUserData(payload: {
    PhoneNumber: string;
  }): Observable<void> {
    const url = `${this.apiUrl}/user/me`;
    return this.authPut<any, any>(url, payload);
  }

  getServiceCategories(): Observable<ServiceCategorySummary[]> {
    const url = `${this.apiBackend}/service-categories`;
    return this.authGet<ServiceCategorySummary[]>(url);
  }

  createServiceCategory(payload: ServiceCategoryCreateDto): Observable<ServiceCategorySummary> {
    const url = `${this.apiBackend}/service-categories`;
    return this.authPost<ServiceCategorySummary, ServiceCategoryCreateDto>(url, payload);
  }

  updateServiceCategory(id: number, payload: ServiceCategoryUpdateDto): Observable<ServiceCategorySummary> {
    const url = `${this.apiBackend}/service-categories/${id}`;
    return this.authPut<ServiceCategorySummary, ServiceCategoryUpdateDto>(url, payload);
  }

  deleteServiceCategory(id: number): Observable<void> {
    const url = `${this.apiBackend}/service-categories/${id}`;
    return this.authDelete<void>(url);
  }

  getJobPositions(): Observable<JobPositionSummary[]> {
    const url = `${this.apiBackend}/job-positions`;
    return this.authGet<JobPositionSummary[]>(url);
  }

  getJobPosition(id: number): Observable<JobPositionDetail> {
    const url = `${this.apiBackend}/job-positions/${id}`;
    return this.authGet<JobPositionDetail>(url);
  }

  createJobPosition(payload: JobPositionCreateDto): Observable<JobPositionDetail> {
    const url = `${this.apiBackend}/job-positions`;
    return this.authPost<JobPositionDetail, JobPositionCreateDto>(url, payload);
  }

  updateJobPosition(id: number, payload: JobPositionUpdateDto): Observable<JobPositionDetail> {
    const url = `${this.apiBackend}/job-positions/${id}`;
    return this.authPut<JobPositionDetail, JobPositionUpdateDto>(url, payload);
  }

  deleteJobPosition(id: number): Observable<void> {
    const url = `${this.apiBackend}/job-positions/${id}`;
    return this.authDelete<void>(url);
  }

  getWorkshopUsers(): Observable<any[]> {
    const url = `${this.apiBackend}/center/users`;
    return this.authGet<any[]>(url);
  }

  updateUserStatus(userId: string, isActive: boolean): Observable<void> {
    const url = `${this.apiBackend}/center/users/${encodeURIComponent(userId)}/status`;
    return this.authPut<void, { isActive: boolean }>(url, { isActive });
  }

  async loadCenter(): Promise<CenterInfo | null> {
    const cached: CenterInfo | null = this.localStorageService.get(LocalStorageKeys.CenterInfo);
    if (cached) return cached;
    try {
      const response = await firstValueFrom(this.getCenterInfo());
      if (!response) throw new Error("Center info is empty");

      this.localStorageService.set(LocalStorageKeys.CenterInfo, response);
      return response;
    } catch (err) {
      console.error("Error loading center info", err);
      return null;
    }
  }
}
